import { api } from '@/utils/api';
import type { FieldProgramConfig } from '@/hooks/useFieldProgramConfig';

export type KycStatus = 'PENDING' | 'MANUAL_REVIEW' | 'APPROVED' | 'REJECTED' | 'RESUBMIT';
export type FieldPartnerTier = 'BRONZE' | 'SILVER' | 'GOLD';
// Back-compat alias — some legacy call sites import PlumberTier.
export type PlumberTier = FieldPartnerTier;
export type ClaimStatus =
  | 'SUBMITTED'
  | 'AUTO_APPROVED'
  | 'AUTO_REJECTED'
  | 'MANUAL_REVIEW'
  | 'APPROVED'
  | 'REJECTED';
export type PayoutStatus = 'QUEUED' | 'PROCESSING' | 'SUCCESS' | 'FAILED';

export interface FieldPartner {
  id: string;
  name: string;
  phone: string;
  email?: string | null;
  region?: string | null;
  kycStatus: KycStatus;
  tier: FieldPartnerTier;
  totalInstallations: number;
  totalEarnings: number;
  createdAt: string;
}

export interface FieldPartnerKyc {
  id: string;
  partnerId: string;
  partnerName: string;
  aadhaarMasked?: string | null;
  pan?: string | null;
  bankAccount?: string | null;
  bankIfsc?: string | null;
  upiVpa?: string | null;
  photoUrls: string[];
  vendorStatus?: string | null;
  status: KycStatus;
  submittedAt: string;
  rejectionReason?: string | null;
}

export interface InstallationClaim {
  id: string;
  claimNumber: string;
  partnerId: string;
  partnerName: string;
  productUnitCode: string;
  productName: string;
  customerName: string;
  customerPhone: string;
  submittedAt: string;
  gpsDistanceM?: number | null;
  autoDecision: 'APPROVE' | 'REJECT' | 'REVIEW';
  status: ClaimStatus;
  photoUrls: string[];
  otpVerified: boolean;
  lat?: number | null;
  lng?: number | null;
  flagReason?: string | null;
}

export interface IncentiveRule {
  id: string;
  name: string;
  productCategory?: string | null;
  perUnitPayout: number;
  upsellBonus?: number | null;
  upsellTrigger?: string | null;
  active: boolean;
  createdAt: string;
}

export interface IncentiveTier {
  id: string;
  tier: FieldPartnerTier;
  minInstallations: number;
  quarterlyGift?: string | null;
  specialReward?: string | null;
  multiplier: number;
}

export interface IncentivePayout {
  id: string;
  partnerId: string;
  partnerName: string;
  amount: number;
  upiVpa?: string | null;
  bankAccount?: string | null;
  status: PayoutStatus;
  providerReferenceId?: string | null;
  triggeredAt: string;
  completedAt?: string | null;
  failureReason?: string | null;
}

export interface FraudFlag {
  id: string;
  type: 'MOCKED_GPS' | 'DUPLICATE_PHOTO' | 'OUT_OF_RANGE' | 'OTP_FAILS';
  partnerId: string;
  partnerName: string;
  claimId?: string | null;
  detail: string;
  detectedAt: string;
  status: 'OPEN' | 'OK' | 'BLOCKED' | 'INVESTIGATING';
}

const BASE = '/field-partners';

export const fieldPartnersApi = {
  // Partners
  list: async (params: Record<string, string> = {}) => {
    const res = await api.get<{ data: FieldPartner[] }>(`${BASE}/partners`, { params });
    return res.data.data ?? [];
  },
  get: async (id: string) => {
    const res = await api.get<{ data: FieldPartner }>(`${BASE}/partners/${id}`);
    return res.data.data;
  },
  create: async (payload: { name: string; phone: string; region?: string; email?: string }) => {
    const res = await api.post(`${BASE}/partners/register`, payload);
    return res.data;
  },

  // KYC — backend exposes /partners/kyc/* (initiate/status/:id/approve/:id/reject)
  // and the directory list (with kycStatus filter) is the queue source.
  listKycQueue: async () => {
    const res = await api.get<{ data: FieldPartner[] }>(`${BASE}/partners`, {
      params: { kycStatus: 'PENDING,MANUAL_REVIEW' },
    });
    return (res.data.data ?? []) as unknown as FieldPartnerKyc[];
  },
  approveKyc: (id: string) => api.patch(`${BASE}/partners/kyc/${id}/approve`),
  rejectKyc: (id: string, reason: string) =>
    api.patch(`${BASE}/partners/kyc/${id}/reject`, { reason }),
  requestKycResubmit: (id: string) =>
    api.patch(`${BASE}/partners/kyc/${id}/reject`, { reason: 'Please resubmit', resubmit: true }),

  // Claims — backend uses PATCH for state changes, not POST
  listClaims: async (params: Record<string, string> = {}) => {
    const res = await api.get<{ data: InstallationClaim[] }>(`${BASE}/claims`, { params });
    return res.data.data ?? [];
  },
  getClaim: async (id: string) => {
    const res = await api.get<{ data: InstallationClaim }>(`${BASE}/claims/${id}`);
    return res.data.data;
  },
  approveClaim: (id: string) => api.patch(`${BASE}/claims/${id}/approve`),
  rejectClaim: (id: string, reason: string) =>
    api.patch(`${BASE}/claims/${id}/reject`, { reason }),
  requestClaimInfo: (id: string) =>
    api.patch(`${BASE}/claims/${id}/reject`, { reason: 'Information requested', requestInfo: true }),

  // Incentive Rules
  listIncentiveRules: async () => {
    const res = await api.get<{ data: IncentiveRule[] }>(`${BASE}/incentive-rules`);
    return res.data.data ?? [];
  },
  createIncentiveRule: (payload: Partial<IncentiveRule>) =>
    api.post(`${BASE}/incentive-rules`, payload),
  updateIncentiveRule: (id: string, payload: Partial<IncentiveRule>) =>
    api.put(`${BASE}/incentive-rules/${id}`, payload),
  deleteIncentiveRule: (id: string) => api.delete(`${BASE}/incentive-rules/${id}`),

  // Incentive Tiers
  listIncentiveTiers: async () => {
    const res = await api.get<{ data: IncentiveTier[] }>(`${BASE}/incentive-tiers`);
    return res.data.data ?? [];
  },
  createIncentiveTier: (payload: Partial<IncentiveTier>) =>
    api.post(`${BASE}/incentive-tiers`, payload),
  updateIncentiveTier: (id: string, payload: Partial<IncentiveTier>) =>
    api.put(`${BASE}/incentive-tiers/${id}`, payload),
  deleteIncentiveTier: (id: string) => api.delete(`${BASE}/incentive-tiers/${id}`),

  // Payouts — backend exposes GET /payouts and POST /payouts/trigger { partnerIds: [] }
  listPayouts: async (params: Record<string, string> = {}) => {
    const res = await api.get<{ data: IncentivePayout[] }>(`${BASE}/payouts`, { params });
    return res.data.data ?? [];
  },
  triggerPayout: (id: string) =>
    api.post(`${BASE}/payouts/trigger`, { partnerIds: [id] }),
  processPending: () =>
    api.post(`${BASE}/payouts/trigger`, { all: true }),
  retryPayout: (id: string) =>
    api.post(`${BASE}/payouts/trigger`, { partnerIds: [id], retry: true }),

  // Fraud — no dedicated backend endpoint yet; surface empty list to keep UI alive.
  // TODO: build /partners/fraud-alerts on backend (Apr 9 sweep finding).
  listFraudAlerts: async () => {
    return [] as FraudFlag[];
  },
  markFraudOk: (_id: string) => Promise.resolve({ data: { success: true } } as any),
  blockPartner: (_id: string) => Promise.resolve({ data: { success: true } } as any),
  resolveFraud: (_id: string, _action: 'MARK_OK' | 'BLOCK' | 'INVESTIGATE') =>
    Promise.resolve({ data: { success: true } } as any),

  // Field Program Config
  getFieldProgramConfig: async () => {
    const res = await api.get<{ data: FieldProgramConfig }>(`${BASE}/field-program-config`);
    return (res.data as any).data ?? res.data;
  },
  updateFieldProgramConfig: (payload: Partial<FieldProgramConfig>) =>
    api.put(`${BASE}/field-program-config`, payload),

  // Cash Reward report
  getCashRewardReport: async (params: Record<string, string> = {}) => {
    const res = await api.get(`${BASE}/cash-rewards/report`, { params });
    return res.data;
  },
};

// Back-compat: legacy call sites still importing `plumbersApi` from '@/api/plumbers'
// will continue to work because plumbers.ts re-exports this object.
export default fieldPartnersApi;
